import React, { useState, useEffect, ChangeEvent } from "react";
import { toast } from "react-toastify";

import Modal from "../../../../components/Reusable/Modal";
import { LabelInput } from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  fetchDGCoinProviderById,
  fetchDGCoinProviders,
  updateDGCoinProvider,
} from "../../../../features/auth/store/dgcoinProviderSlice";
import { EditProps } from "../types";

export const EditDGCoinProviderModal: React.FC<EditProps> = ({
  isOpen,
  id,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { dgcoinProviders, loading } = useAppSelector(
    (state) => state.dgcoinProviders
  );

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "on",
    recipient_label: "",
    additional_input: false,
    additional_input_placeholder: "",
    additional_input_type: "",
    additional_input_label: "",
    additional_input_form_name: "",
  });
  const [image, setImage] = useState<File | undefined>(undefined);

  useEffect(() => {
    if (isOpen && id) {
      dispatch(fetchDGCoinProviderById(id));
    }
  }, [dispatch, isOpen, id]);

  useEffect(() => {
    if (id) {
      const providerToEdit = dgcoinProviders.find(
        (provider) => provider.id === id
      );
      if (providerToEdit) {
        setFormData({
          name: providerToEdit.name,
          description: providerToEdit.description || "",
          status: providerToEdit.status || "on",
          recipient_label: providerToEdit.recipient_label || "",
          additional_input: Boolean(providerToEdit.additional_input),
          additional_input_placeholder:
            providerToEdit.additional_input_placeholder || "",
          additional_input_type: providerToEdit.additional_input_type || "",
          additional_input_label: providerToEdit.additional_input_label || "",
          additional_input_form_name:
            providerToEdit.additional_input_form_name || "",
        });
      }
    }
  }, [id, dgcoinProviders]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.currentTarget.name]: e.currentTarget.value,
    }));
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.currentTarget.name]: e.currentTarget.checked,
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setImage(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    const formDataObj = new FormData();
    formDataObj.append("name", formData.name);
    formDataObj.append("description", formData.description);
    formDataObj.append("status", formData.status);
    formDataObj.append("recipient_label", formData.recipient_label);
    formDataObj.append(
      "additional_input",
      formData.additional_input ? "1" : "0"
    );
    formDataObj.append(
      "additional_input_placeholder",
      formData.additional_input_placeholder
    );
    formDataObj.append("additional_input_type", formData.additional_input_type);
    formDataObj.append(
      "additional_input_label",
      formData.additional_input_label
    );
    formDataObj.append(
      "additional_input_form_name",
      formData.additional_input_form_name
    );

    if (image) {
      formDataObj.append("image", image);
    }
    try {
      await dispatch(updateDGCoinProvider({ id, data: formDataObj })).unwrap();
      toast.success("DG Coin provider updated successfully!");
      handleClose();
      dispatch(fetchDGCoinProviders());
    } catch (error: any) {
      toast.error(error?.message || "Failed to update DG Coin provider");
      console.error("Submission error:", error);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    setFormData({
      name: "",
      description: "",
      status: "on",
      recipient_label: "",
      additional_input: false,
      additional_input_placeholder: "",
      additional_input_type: "",
      additional_input_label: "",
      additional_input_form_name: "",
    });
    setImage(undefined);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white p-6 w-[400px] max-h-[90vh] overflow-y-auto dark:bg-black dark:text-white">
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-base font-semibold">Edit DG Coin Provider</p>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="name"
              placeholder="Name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="description"
              placeholder="Description"
              value={formData.description}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="status"
              placeholder="Status (on/off)"
              value={formData.status}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="recipient_label"
              placeholder="Recipient Label"
              value={formData.recipient_label}
              onChange={handleInputChange}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="additional_input"
              type="checkbox"
              name="additional_input"
              checked={formData.additional_input}
              onChange={handleCheckboxChange}
            />
            <label htmlFor="additional_input" className="text-sm text-gray-500">
              Additional Input
            </label>
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="additional_input_placeholder"
              placeholder="Additional Input Placeholder"
              value={formData.additional_input_placeholder}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="additional_input_type"
              placeholder="Additional Input Type"
              value={formData.additional_input_type}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="additional_input_label"
              placeholder="Additional Input Label"
              value={formData.additional_input_label}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="additional_input_form_name"
              placeholder="Additional Input Form Name"
              value={formData.additional_input_form_name}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm text-gray-500">Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500"
            />
            {image && (
              <div className="flex items-center gap-2">
                <span className="text-xs">{image.name}</span>
                <button
                  type="button"
                  className="text-red-500 text-xs"
                  onClick={removeImage}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              onClick={handleClose}
              bgColor="bg-gray-200"
              textColor="text-gray-700"
              borderRadius="rounded-lg"
              className="px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="px-4 py-2"
              disabled={loading}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
